import React, { useEffect, useState } from 'react';
import { useTalentos } from '../../../../application/talentos/hooks/useTalentos';
import ToolbarComponent from '../../../ui/section';
import DynamicTable from '../../../ui/table2';
import Loader from '../../../ui/loader';
import { IconButton } from '../../../ui/button';
import TalentosAPI from '../../../../api/talentos';

const EmployersPage = (props) => {
    const {
        getEmployers,
        isLoading,
        error
    } = useTalentos();

    const [employers, setEmployers] = useState([]);
    const [companies, setCompanies] = useState({}); // Cache de compañías por ID

    const columns = [
        { key: 'employer', label: 'Empleador', style: { width: "20%" } },
        { key: 'email', label: 'Email', style: { width: "20%" } },
        { key: 'company', label: 'Compañía', style: { width: "20%" } },
        { key: 'location', label: 'Ubicación', style: { width: "15%" } },
        { key: 'createdAt', label: 'Registro', style: { width: "15%" } },
        { key: 'actions', label: 'Acciones', style: { width: "10%" } }
    ];

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const customElements = {
        employer: (data) => {
            const fullName = `${data.name || ''} ${data.lastname || ''}`.trim();
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontWeight: 500, color: '#111827' }}>
                        {fullName || '-'}
                    </span>
                    {data.phone && (
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>
                            {data.phone}
                        </span>
                    )}
                </div>
            );
        },
        email: (data) => {
            if (!data.email) return <span>-</span>;
            return (
                <a 
                    href={`mailto:${data.email}`}
                    style={{ 
                        color: '#3B82F6', 
                        textDecoration: 'none',
                        fontSize: '14px'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                    {data.email}
                </a>
            );
        },
        company: (data) => {
            let companyData = null;
            let companyId = null;

            // Determinar el ID de la compañía y obtener los datos
            if (data.company && typeof data.company === 'object') {
                if (data.company.name) {
                    // Ya tiene datos completos
                    companyData = data.company;
                } else if (data.company._id) {
                    // Solo tiene ID, buscar en cache
                    companyId = data.company._id;
                    companyData = companies[companyId];
                }
            } else if (typeof data.company === 'string') {
                // Es un ID string, buscar en cache
                companyId = data.company;
                companyData = companies[companyId];
            }

            // Si no tenemos los datos y es un ID, intentar cargar
            if (!companyData && companyId && !companies[companyId]) {
                loadCompany(companyId);
                return <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Cargando...</span>;
            }

            // Mostrar los datos de la compañía
            if (companyData && companyData.name) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {companyData.logo && (
                            <img 
                                src={companyData.logo} 
                                alt={companyData.name}
                                style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '4px',
                                    objectFit: 'cover'
                                }}
                            />
                        )}
                        <span style={{ fontWeight: 500 }}>{companyData.name}</span>
                    </div>
                );
            }

            return <span style={{ color: '#9CA3AF' }}>-</span>;
        },
        location: (data) => {
            const locationParts = [];
            if (data.city) locationParts.push(data.city);
            if (data.country) locationParts.push(data.country);
            
            if (locationParts.length === 0) return <span style={{ color: '#9CA3AF' }}>-</span>;
            
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {data.city && (
                        <span style={{ fontSize: '14px', color: '#111827' }}>{data.city}</span>
                    )}
                    {data.country && (
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{data.country}</span>
                    )}
                </div>
            );
        },
        createdAt: (data) => {
            return (
                <span style={{ fontSize: '13px', color: '#6B7280' }}>
                    {formatDate(data.createdAt)}
                </span>
            );
        },
        actions: (data) => (
            <div style={{ display: 'flex', gap: '8px' }}>
                <IconButton onClick={() => handleView(data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42004 13.98 8.42004 12C8.42004 10.02 10.02 8.42004 12 8.42004C13.98 8.42004 15.58 10.02 15.58 12Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.46997 3.71997 5.17997 5.79997 2.88997 9.39997C1.98997 10.81 1.98997 13.18 2.88997 14.59C5.17997 18.19 8.46997 20.27 12 20.27Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </div>
        )
    };

    useEffect(() => {
        loadEmployers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCompany = async (companyId) => {
        // Si ya tenemos la compañía en cache, no la cargamos de nuevo
        if (companies[companyId]) {
            return companies[companyId];
        }

        try {
            const companyData = await TalentosAPI.getCompanyById(companyId);
            setCompanies(prev => ({
                ...prev,
                [companyId]: companyData
            }));
            return companyData;
        } catch (err) {
            console.error('Error al cargar compañía:', err);
            return null;
        }
    };

    const loadEmployers = async () => {
        try {
            const data = await getEmployers({ limit: 100 });
            const employersData = Array.isArray(data) ? data : [];
            setEmployers(employersData);

            // Cargar compañías para los empleadores que tienen company como ID
            const companyIds = new Set();
            employersData.forEach(employer => {
                if (employer.company) {
                    if (typeof employer.company === 'string') {
                        companyIds.add(employer.company);
                    } else if (typeof employer.company === 'object' && employer.company._id && !employer.company.name) {
                        companyIds.add(employer.company._id);
                    }
                }
            });

            // Cargar todas las compañías en paralelo
            const companiesPromises = Array.from(companyIds).map(id => loadCompany(id));
            await Promise.all(companiesPromises);
        } catch (err) {
            console.error('Error al cargar empleadores:', err);
        }
    };

    const handleView = (id) => {
        props.history.push(`/talentos/employers/${id}`);
    };

    return (
        <>
            <ToolbarComponent label="Empleadores" showBackButton={false}>
                {error && (
                    <div style={{ padding: '10px', background: '#FEE2E2', color: '#DC2626', borderRadius: '4px', marginBottom: '16px' }}>
                        {error}
                    </div>
                )}
                <DynamicTable
                    headers={columns}
                    data={employers}
                    customElements={customElements}
                    fullPage={true}
                    showLabel={false}
                    loaderComponent={<Loader text="Cargando empleadores" />}
                    isLoading={isLoading}
                />
            </ToolbarComponent>
        </>
    );
};

export default EmployersPage;

